<template>
  <div class="logo-uploader">
    <div
      class="logo-dropzone"
      :class="{
        'is-dragover': isDragging,
        'has-image': !!previewUrl,
        'is-disabled': disabled
      }"
      @click="openPicker"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        ref="fileInput"
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        class="logo-input"
        :disabled="disabled || isUploading"
        @change="onFileChange"
      />

      <div v-if="isUploading" class="logo-state">
        <CSpinner size="sm" class="logo-state__spinner" />
        <span>Uploading to R2…</span>
      </div>

      <template v-else-if="previewUrl">
        <img :src="previewUrl" :alt="alt" class="logo-preview" />
        <div class="logo-preview__caption">
          <span class="logo-preview__hint">Click or drop to replace</span>
          <button
            type="button"
            class="logo-preview__remove"
            :disabled="disabled"
            @click.stop="removeLogo"
          >
            Remove
          </button>
        </div>
      </template>

      <template v-else>
        <div class="logo-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </div>
        <div class="logo-prompt">
          <span class="logo-prompt__title">{{ promptTitle }}</span>
          <span class="logo-prompt__hint">{{ promptHint }}</span>
        </div>
      </template>
    </div>

    <div v-if="errorMessage" class="logo-error">{{ errorMessage }}</div>
    <div v-else-if="value" class="logo-meta" :title="value">
      <span class="logo-meta__label">Stored at</span>
      <code class="logo-meta__url">{{ value }}</code>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const ACCEPTED_MIMES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']
const MAX_BYTES = 2 * 1024 * 1024

export default {
  name: 'OrganisationLogoUploader',
  props: {
    value: { type: String, default: '' },
    alt: { type: String, default: 'Organisation logo' },
    disabled: { type: Boolean, default: false },
    // Override these to reuse the component for other entities (e.g.
    // branches). Defaults preserve the original organisation behaviour
    // so existing call sites are unaffected.
    uploadPath: { type: String, default: '/api/organisations/upload-logo' },
    formField:  { type: String, default: 'logo' },
    promptTitle: { type: String, default: 'Drop logo or click to upload' },
    promptHint:  { type: String, default: 'PNG, JPG, WebP or SVG · up to 2 MB' },
    errorFallback: { type: String, default: 'Logo upload failed. Check that R2 is configured and try again.' }
  },
  data () {
    return {
      isDragging: false,
      isUploading: false,
      errorMessage: '',
      localPreview: ''
    }
  },
  computed: {
    previewUrl () {
      return this.localPreview || this.value || ''
    }
  },
  methods: {
    openPicker () {
      if (this.disabled || this.isUploading) return
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    onDragOver () {
      if (this.disabled || this.isUploading) return
      this.isDragging = true
    },
    onDragLeave () {
      this.isDragging = false
    },
    onDrop (event) {
      this.isDragging = false
      if (this.disabled || this.isUploading) return
      const file = event.dataTransfer?.files?.[0]
      if (file) this.uploadFile(file)
    },
    onFileChange (event) {
      const file = event.target.files?.[0]
      if (file) this.uploadFile(file)
      event.target.value = ''
    },
    validate (file) {
      if (!ACCEPTED_MIMES.includes(file.type)) {
        return 'Use a PNG, JPG, WebP or SVG image.'
      }
      if (file.size > MAX_BYTES) {
        return 'Image must be 2 MB or smaller.'
      }
      return ''
    },
    uploadFile (file) {
      const validationError = this.validate(file)
      if (validationError) {
        this.errorMessage = validationError
        return
      }

      this.errorMessage = ''
      this.isUploading = true

      const reader = new FileReader()
      reader.onload = (e) => { this.localPreview = e.target.result }
      reader.readAsDataURL(file)

      const formData = new FormData()
      formData.append(this.formField, file)
      const token = localStorage.getItem('api_token')

      // Allow either an absolute URL or a "/api/..." path; the latter is
      // joined onto $apiAdress so call sites stay terse.
      const endpoint = /^https?:\/\//.test(this.uploadPath)
        ? `${this.uploadPath}?token=${token}`
        : `${this.$apiAdress}${this.uploadPath}?token=${token}`

      axios.post(
        endpoint,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
        .then((response) => {
          const validationErrors = response.data?.error
          if (validationErrors && typeof validationErrors === 'object') {
            const firstKey = Object.keys(validationErrors)[0]
            const value = validationErrors[firstKey]
            this.errorMessage = Array.isArray(value) ? value[0] : String(value)
            this.localPreview = ''
            return
          }

          const url = response.data?.data?.url
          if (!url) {
            this.errorMessage = 'Upload succeeded but no URL was returned.'
            this.localPreview = ''
            return
          }

          this.localPreview = ''
          this.$emit('input', url)
          this.$emit('uploaded', response.data.data)
        })
        .catch((error) => {
          console.log(error)
          const data = error?.response?.data
          this.errorMessage = data?.message
            || (data?.error && typeof data.error === 'object' ? Object.values(data.error)[0]?.[0] : '')
            || this.errorFallback
          this.localPreview = ''
        })
        .finally(() => {
          this.isUploading = false
        })
    },
    removeLogo () {
      if (this.disabled) return
      this.localPreview = ''
      this.errorMessage = ''
      this.$emit('input', '')
    }
  }
}
</script>

<style scoped>
.logo-uploader {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.logo-dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 168px;
  padding: 1.1rem;
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
  background: #fbfcfe;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.logo-dropzone:hover:not(.is-disabled) {
  border-color: #16a07a;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.08);
}

.logo-dropzone.is-dragover {
  border-color: #0e5b46;
  background: #ecfdf5;
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.18);
}

.logo-dropzone.has-image {
  background: #ffffff;
  border-style: solid;
  border-color: #e5e9f2;
}

.logo-dropzone.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.logo-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.logo-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #ecfdf5;
  color: #0e5b46;
}

.logo-prompt {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.logo-prompt__title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
}

.logo-prompt__hint {
  font-size: 0.74rem;
  color: #6b7280;
}

.logo-state {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: #4b5563;
  font-weight: 600;
}

.logo-preview {
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
  border-radius: 8px;
  background: #ffffff;
}

.logo-preview__caption {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.74rem;
  color: #6b7280;
}

.logo-preview__hint {
  font-weight: 500;
}

.logo-preview__remove {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.logo-preview__remove:hover:not(:disabled) {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #991b1b;
}

.logo-preview__remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.logo-error {
  font-size: 0.74rem;
  color: #b91c1c;
  font-weight: 600;
}

.logo-meta {
  display: inline-flex;
  align-items: baseline;
  gap: 0.4rem;
  font-size: 0.72rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.logo-meta__label {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4b5563;
  font-size: 0.66rem;
}

.logo-meta__url {
  background: #f1f5f9;
  color: #1f2a44;
  padding: 0.05rem 0.4rem;
  border-radius: 4px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.7rem;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
